/**
 * VesselFinder AIS vessel tracking data source.
 * Fetches live vessel positions from the VesselFinder API.
 * Requires API key.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'vesselfinder';

/**
 * AIS ship type codes → human-readable labels.
 * Based on the AIS specification (ITU-R M.1371).
 */
function shipTypeLabel(code: number): string {
  if (code >= 70 && code <= 79) return 'Cargo';
  if (code >= 80 && code <= 89) return 'Tanker';
  if (code >= 60 && code <= 69) return 'Passenger';
  if (code >= 40 && code <= 49) return 'High-Speed Craft';
  if (code >= 50 && code <= 59) return 'Special Craft';
  if (code === 30) return 'Fishing';
  if (code === 31 || code === 32) return 'Towing';
  if (code === 33) return 'Dredging';
  if (code === 34) return 'Diving';
  if (code === 35) return 'Military';
  if (code === 36) return 'Sailing';
  if (code === 37) return 'Pleasure Craft';
  return 'Other';
}

/** Map an AIS ship-type code to the vessel color category used by the map. */
export function shipTypeCategory(code: number): string {
  if (code >= 70 && code <= 79) return 'cargo';
  if (code >= 80 && code <= 89) return 'tanker';
  if (code >= 60 && code <= 69) return 'passenger';
  if (code === 35) return 'military';
  if (code === 30) return 'fishing';
  return 'other';
}

interface VesselRecord {
  AIS: {
    MMSI: number;
    NAME: string;
    LATITUDE: number;
    LONGITUDE: number;
    SPEED: number;
    HEADING: number;
    COURSE: number;
    SHIPTYPE: number;
    FLAG: string;
    DESTINATION: string;
    IMO: number;
    TIMESTAMP: string;
  };
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const vesselSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'VesselFinder',
    description: 'Live vessel positions via AIS data',
    icon: 'anchor',
    category: 'maritime',
    updateInterval: 60_000, // 1 min
    requiresApiKey: true,
    apiKeyUrl: 'https://www.vesselfinder.com/api',
    dataLicense: 'VesselFinder API Terms',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    { key: 'vesselfinder_api_key', label: 'VesselFinder API Key', type: 'password', required: true },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] VesselFinder vessel source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Proxy handles API key injection
      const url = `${getApiBase()}/api/data/vessels`;

      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`VesselFinder API returned ${response.status}`);
      }

      const data: VesselRecord[] = await response.json();

      if (!Array.isArray(data)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const vessel of data) {
        const ais = vessel.AIS;
        if (!ais || ais.LATITUDE == null || ais.LONGITUDE == null) continue;

        const typeCode = ais.SHIPTYPE || 0;
        const category = shipTypeCategory(typeCode);
        const label = shipTypeLabel(typeCode);

        // Epoch-bucketed ID: position updates must pass through pipeline dedup.
        // Bucket to the update interval (60s) so each fetch cycle generates new IDs.
        const ts = ais.TIMESTAMP ? new Date(ais.TIMESTAMP).getTime() : Date.now();
        const epochBucket = Math.floor(ts / 60_000);
        events.push({
          id: `vessel-${ais.MMSI}-${epochBucket}`,
          source: SOURCE_ID,
          timestamp: ais.TIMESTAMP ? new Date(ais.TIMESTAMP).toISOString() : new Date().toISOString(),
          type: 'vessel_position',
          title: `${ais.NAME || `MMSI ${ais.MMSI}`} (${label}) — ${ais.FLAG || 'Unknown'}`,
          severity: 'info',
          location: {
            lat: ais.LATITUDE,
            lng: ais.LONGITUDE,
          },
          tags: ['maritime', category, ais.FLAG].filter(Boolean),
          raw: {
            mmsi: ais.MMSI,
            speed: ais.SPEED,
            heading: ais.HEADING,
            course: ais.COURSE,
            destination: ais.DESTINATION,
            shipType: typeCode,
            shipTypeLabel: label,
            vesselType: shipTypeCategory(typeCode),
            imo: ais.IMO,
            flag: ais.FLAG,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
